import {
  Box,
  Table,
  Tr,
  Th,
  Text,
  Thead,
  Tbody,
  Td,
  Flex,
  Alert,
  AlertIcon,
  Divider,
} from '@chakra-ui/react';
import React from 'react';
import { Link } from 'react-router-dom';

import { WipConfig } from '../../types';
import { useDraggableRow } from 'packages/common-frontend/components/DragAndDropTable/hooks/useDraggableRow';
import { TrDraggableHandle } from 'packages/common-frontend/components/DragAndDropTable/ui/TrDraggableHandle';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

// Helpers
const getTypeSpecificFields = (baseField: any) => {
  switch (baseField.type) {
    case 'boolean':
    case 'optionalBoolean':
      return <Text>externalBooleanTrue: {baseField.externalBooleanTrue}</Text>;
    case 'currency':
    case 'optionalCurrency':
      return <Text>currencyCode: {baseField.currencyCode}</Text>;
    case 'date':
    case 'optionalDate':
      return <Text>externalDateFormat: {baseField.externalDateFormat}</Text>;
    case 'dateTime':
    case 'optionalDateTime':
      return (
        <>
          <Text>
            externalDateTimeFormat: {baseField.externalDateTimeFormat}
          </Text>
          {baseField.defaultTz && <Text>defaultTz: {baseField.defaultTz}</Text>}
        </>
      );
    default:
      return '';
  }
};

// Exports
type LoanProductBaseFieldConfigProps = {
  creditFacilityId: string;
  fields: CreditAgreement.DataTapeField.BaseConfig[];
  wipConfig?: WipConfig;
};
export const LoanProductBaseFieldConfig = ({
  creditFacilityId,
  fields,
  wipConfig,
}: LoanProductBaseFieldConfigProps) => {
  const flags = useFeatureFlags();

  const {
    useRowState,
    useOrderLockedState,
    moveRow,
  } = useDraggableRow<CreditAgreement.DataTapeField.BaseConfig>({
    comparisonKey: 'field',
  });
  const [baseFieldsState] = useRowState(fields);
  const [isOrderLocked] = useOrderLockedState();

  const isClientWipMode = wipConfig?.operationMode === 'client';

  return (
    <>
      <Box mb="35px" width="100%">
        {wipConfig && (
          <Alert status="warning" variant="subtle">
            <AlertIcon />
            <Flex>
              <Text> Base fields can be edited&nbsp;</Text>
              {(() => {
                // Client edit mode
                if (isClientWipMode) {
                  if (flags.enableLenderBaseFieldEdits && creditFacilityId) {
                    return (
                      <Link
                        to={`/settings/credit-facilities/${creditFacilityId}/borrowing-base/loantape`}
                        style={{ textDecoration: 'underline' }}
                      >
                        in the Loan Tape tab.
                      </Link>
                    );
                  }
                  return <Text>by your Finley team.</Text>;
                }

                // Admin edit mode
                return <Text>in tape-builder.</Text>;
              })()}
            </Flex>
          </Alert>
        )}
        <Divider mt="20px" />
        <Flex alignItems="center" mb="2" mt="2">
          <Text fontWeight="bold" fontSize="18px">
            Base Field Configuration
          </Text>
        </Flex>
        {baseFieldsState.length === 0 && (
          <Text>
            There are currently no configurations for this loan product.
          </Text>
        )}
        {baseFieldsState.length > 0 && (
          <Flex width="100%" overflowY="auto">
            <Table>
              <Thead>
                <Tr>
                  <Th minWidth="12.5rem">Field Name</Th>
                  <Th minWidth="7.5rem">Type</Th>
                  <Th minWidth="8rem">Default Value</Th>
                  <Th minWidth="20rem">Other Properties</Th>
                </Tr>
              </Thead>
              <Tbody>
                {baseFieldsState.map((baseField: any, index: number) => {
                  return (
                    <TrDraggableHandle
                      acceptedType="baseField"
                      id={baseField.field}
                      index={index}
                      key={baseField.field}
                      moveRow={moveRow}
                      isDragDisabled={isOrderLocked}
                    >
                      <Td>{baseField.field}</Td>
                      <Td>{baseField.type}</Td>
                      <Td>{baseField.defaultTo || '-'}</Td>
                      <Td>{getTypeSpecificFields(baseField)}</Td>
                    </TrDraggableHandle>
                  );
                })}
              </Tbody>
            </Table>
          </Flex>
        )}
      </Box>
    </>
  );
};
